/**
 * Wrap Text + Single Line Spacing (Format Tools › Tables & Text) — ported from
 * the VSTO plugin's `TextandParagraphFunctions.WrapText` / `.LineSpacing`.
 *
 * Wrap Text maps straight onto `TextFrame.wordWrap`, so it stays on the fast
 * native path. Line spacing does not: Office.js's `ParagraphFormat` exposes
 * only alignment, bullets and indent — there is no line-spacing property in any
 * requirement set — so it goes through the OOXML slide rewrite and writes the
 * `a:lnSpc`/`a:spcBef`/`a:spcAft` that VSTO set via
 * `SpaceWithin = 1; SpaceBefore = 0; SpaceAfter = 0`.
 */
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { NS_A, NS_P } from "../../../const";
import { rewriteActiveSlide } from "./slideRewrite";
import { findSelectedShapeInSpTree } from "./slideOoxml";

/** OOXML spells 100% line spacing as 100000 (percent × 1000). */
const SINGLE_SPACING_PCT = "100000";

/** Placeholder types VSTO's LineSpacing skipped (`shape != Shapes.Title`). */
const TITLE_PLACEHOLDERS = new Set(["title", "ctrTitle"]);

/**
 * Toggle word wrap on every selected shape that has a text frame.
 *
 * VSTO flipped each shape independently, so a mixed selection ended up
 * inconsistent. Here the whole selection is driven to a single state — off if
 * *any* shape currently wraps, on otherwise — which is what a toggle button on
 * a multi-selection is expected to do.
 */
export async function toggleWrapText(): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");

  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select at least one shape first.");

    const frames = sel.items.map((shape) => shape.getTextFrameOrNullObject());
    for (const frame of frames) frame.load("isNullObject,wordWrap");
    await context.sync();

    const usable = frames.filter((frame) => !frame.isNullObject);
    if (usable.length === 0) {
      throw new Error("None of the selected objects hold text — select a text box or shape.");
    }

    const next = !usable.some((frame) => frame.wordWrap);
    for (const frame of usable) frame.wordWrap = next;
    await context.sync();
  });
}

/** True when this `<p:sp>` is the slide's title placeholder. */
function isTitlePlaceholder(el: Element): boolean {
  for (const ph of Array.from(el.getElementsByTagNameNS(NS_P, "ph"))) {
    if (TITLE_PLACEHOLDERS.has(ph.getAttribute("type") ?? "")) return true;
  }
  return false;
}

/** Replace `<a:pPr>`'s existing child of `localName` with a fresh one. */
function replaceSpacingChild(pPr: Element, localName: string, build: (el: Element) => void): void {
  for (const existing of Array.from(pPr.getElementsByTagNameNS(NS_A, localName))) existing.remove();
  const el = pPr.ownerDocument.createElementNS(NS_A, `a:${localName}`);
  build(el);
  // Schema order inside a:pPr puts lnSpc, spcBef then spcAft ahead of every
  // other child, so each one is inserted at the front and the three end up in
  // order because they're written spcAft → spcBef → lnSpc.
  pPr.prepend(el);
}

/** Set single spacing with no space before/after on one paragraph. */
function applySingleSpacingToParagraph(paragraph: Element): void {
  const doc = paragraph.ownerDocument;
  let pPr = paragraph.getElementsByTagNameNS(NS_A, "pPr")[0];
  if (!pPr) {
    pPr = doc.createElementNS(NS_A, "a:pPr");
    paragraph.prepend(pPr); // a:pPr is always a:p's first child
  }

  const zeroPoints = (el: Element): void => {
    const pts = doc.createElementNS(NS_A, "a:spcPts");
    pts.setAttribute("val", "0");
    el.append(pts);
  };
  replaceSpacingChild(pPr, "spcAft", zeroPoints);
  replaceSpacingChild(pPr, "spcBef", zeroPoints);
  replaceSpacingChild(pPr, "lnSpc", (el) => {
    const pct = doc.createElementNS(NS_A, "a:spcPct");
    pct.setAttribute("val", SINGLE_SPACING_PCT);
    el.append(pct);
  });
}

/**
 * Apply single line spacing to every paragraph under `el`.
 *
 * `getElementsByTagNameNS` walks the whole subtree, so groups (`p:grpSp`) and
 * tables (`p:graphicFrame` → `a:tbl` → cells) are covered by the same call that
 * needed two extra recursive helpers in VSTO.
 */
function applySingleSpacing(el: Element): number {
  const paragraphs = Array.from(el.getElementsByTagNameNS(NS_A, "p"));
  for (const paragraph of paragraphs) applySingleSpacingToParagraph(paragraph);
  return paragraphs.length;
}

/**
 * Set single line spacing (and zero space before/after) on the selected
 * shapes, groups and tables, skipping the slide's title placeholder.
 *
 * NOTE: this replaces the slide (see `rewriteActiveSlide`), so the selection is
 * cleared and it undoes as one step — the panel says so before running it.
 */
export async function applySingleLineSpacing(): Promise<void> {
  await rewriteActiveSlide(({ selection, spTree }) => {
    const used = new Set<Element>();
    let paragraphs = 0;
    for (const shape of selection) {
      const el = findSelectedShapeInSpTree(spTree, shape, used);
      if (!el) continue;
      used.add(el);
      if (isTitlePlaceholder(el)) continue; // VSTO skipped the title too
      paragraphs += applySingleSpacing(el);
    }
    return paragraphs;
  }, "None of the selected objects hold paragraphs to space — select a text box, group or table.");
}
